import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  closestCorners, useDroppable
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Plus, AlertTriangle, Calendar, GripVertical, Download, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { cn } from '../lib/utils';
import { getCompanyFilterLabel } from '../lib/serviceGroups';

const PHASES = [
  { id: 1, label: 'Phase 1', sublabel: 'Erfassung', color: 'bg-slate-100 border-slate-200 dark:bg-slate-800/50 dark:border-slate-700' },
  { id: 2, label: 'Phase 2', sublabel: 'Terminierung', color: 'bg-blue-50 border-blue-100 dark:bg-blue-900/20 dark:border-blue-800/50' },
  { id: 3, label: 'Phase 3', sublabel: 'Rapportieren', color: 'bg-amber-50 border-amber-100 dark:bg-amber-900/20 dark:border-amber-800/50' },
  { id: 4, label: 'Phase 4', sublabel: 'Verrechnen', color: 'bg-purple-50 border-purple-100 dark:bg-purple-900/20 dark:border-purple-800/50' },
];
const PHASE_BADGE = ['', 'phase-1', 'phase-2', 'phase-3', 'phase-4'];

const getDeadlineBadge = (dateValue, label = 'Deadline') => {
  if (!dateValue) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const daysUntil = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (daysUntil <= 0) {
    return {
      label: `${Math.abs(daysUntil)}T`,
      title: `Tage seit ${label}`,
      tone: 'red',
      color: 'bg-red-100 border-red-400 text-red-700',
      border: 'border-red-400',
    };
  }
  if (daysUntil <= 3) {
    return {
      label: `${daysUntil}T`,
      title: `Tage bis ${label}`,
      tone: 'orange',
      color: 'bg-amber-100 border-amber-400 text-amber-700',
      border: 'border-amber-400',
    };
  }
  return null;
};

const OrderCard = ({ order, isDragging = false }) => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const dueDate = order.phase === 4 ? order.invoice_due_date : order.deadline;
  const dueLabel = order.phase === 4 ? 'Zahlungsfrist' : 'Deadline';
  const isOverdue = dueDate && dueDate <= today;
  const deadlineBadge = getDeadlineBadge(dueDate, dueLabel);

  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: order.id });

  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.4 : 1 } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`order-card-${order.id}`}
      className={cn(
        "bg-card border rounded-sm p-4 cursor-pointer hover:shadow-sm transition-all group select-none",
        deadlineBadge ? `border-2 ${deadlineBadge.border}` : "border-border/60 hover:border-primary/30",
        isDragging ? "opacity-40" : ""
      )}
    >
      <div className="flex items-start gap-2">
        <div
          {...attributes}
          {...listeners}
          className="mt-0.5 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground transition-colors shrink-0"
          onClick={e => e.stopPropagation()}
        >
          <GripVertical className="w-3.5 h-3.5" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0" onClick={() => navigate(`/orders/${order.id}`)}>
          <div className="flex items-start justify-between mb-1.5">
            <span className="text-xs font-mono text-muted-foreground">{order.order_number}</span>
            <div className="flex items-center gap-1">
              {deadlineBadge && (
                <span className={cn("text-xs font-bold px-1.5 py-0.5 rounded border", deadlineBadge.color)} title={deadlineBadge.title}>
                  {deadlineBadge.label}
                </span>
              )}
              {deadlineBadge && (
                <AlertTriangle
                  className={cn("w-3.5 h-3.5 shrink-0", deadlineBadge.tone === 'red' ? 'text-destructive' : 'text-amber-600')}
                  strokeWidth={2}
                />
              )}
            </div>
          </div>
          <p className="text-sm font-semibold text-foreground line-clamp-2 mb-2">{order.title}</p>
          {order.customer_name && <p className="text-xs text-muted-foreground truncate mb-0.5">{order.customer_name}</p>}
          {order.property_address && <p className="text-xs text-muted-foreground truncate">{order.property_address}</p>}
          {getCompanyFilterLabel(order) && <p className="text-xs text-muted-foreground truncate mt-0.5">{getCompanyFilterLabel(order)}</p>}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/40">
            {dueDate ? (
              <div className={cn("flex items-center gap-1 text-xs", isOverdue ? "text-destructive" : deadlineBadge ? "text-amber-600" : "text-muted-foreground")}>
                <Calendar className="w-3 h-3" strokeWidth={1.5} />
                <span className="font-mono" title={dueLabel}>{dueDate}</span>
              </div>
            ) : <span />}
            {order.totals?.total > 0 && (
              <span className="text-xs font-mono font-medium">CHF {order.totals.total.toFixed(2)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PhaseColumn = ({ phase, orders, isOver }) => {
  const { setNodeRef } = useDroppable({ id: `phase-${phase.id}` });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "w-72 shrink-0 border rounded-sm p-3 transition-colors",
        phase.color,
        isOver ? "ring-2 ring-primary ring-offset-1" : ""
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{phase.label}</p>
          <p className="text-sm font-medium text-foreground">{phase.sublabel}</p>
        </div>
        <span className={cn("text-xs font-mono font-bold px-2 py-0.5 rounded-sm", PHASE_BADGE[phase.id])}>
          {orders.length}
        </span>
      </div>
      <div className="space-y-2 min-h-[120px] max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
        {orders.length === 0 ? (
          <div className={cn("py-8 text-center rounded-sm border-2 border-dashed transition-colors", isOver ? "border-primary/50 bg-primary/5" : "border-transparent")}>
            <p className="text-xs text-muted-foreground">{isOver ? "Hier ablegen" : "Keine Aufträge"}</p>
          </div>
        ) : (
          orders.map(order => <OrderCard key={order.id} order={order} />)
        )}
      </div>
    </div>
  );
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [sortMode, setSortMode] = useState('due');
  const navigate = useNavigate();
  const { hasPermission, canEditPhase, isViewer } = useAuth();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const loadOrders = useCallback(async () => {
    try {
      const res = await api.get('/orders');
      setOrders(res.data);
    } catch {
      toast.error('Aufträge konnten nicht geladen werden');
    }
  }, []);

  useEffect(() => {
    loadOrders().finally(() => setLoading(false));
  }, [loadOrders]);

  const monteurCompanies = Array.from(new Set(orders.map(getCompanyFilterLabel).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'de-CH'));
  const customerNames = Array.from(new Set(orders.map(o => (o.customer_name || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'de-CH'));
  const normalizedSearch = search.trim().toLowerCase();
  const visibleOrders = orders.filter(o => {
    if (companyFilter !== 'all' && getCompanyFilterLabel(o) !== companyFilter) return false;
    if (customerFilter !== 'all' && (o.customer_name || '').trim() !== customerFilter) return false;
    if (!normalizedSearch) return true;
    return [
      o.order_number, o.title, o.customer_name, o.property_address, o.object_name,
      o.renter_name, o.monteur_company, o.monteur_name, getCompanyFilterLabel(o), o.deadline, o.invoice_due_date,
    ].some(v => (v || '').toString().toLowerCase().includes(normalizedSearch));
  });

  const sortValue = (order) => {
    if (sortMode === 'company') return getCompanyFilterLabel(order).toLowerCase();
    if (sortMode === 'customer') return (order.customer_name || '').toLowerCase();
    if (sortMode === 'amount') return order.totals?.total || 0;
    if (sortMode === 'created') return order.created_at || '';
    return order.phase === 4 ? (order.invoice_due_date || '') : (order.deadline || '');
  };

  const ordersByPhase = (phase) => {
    const filtered = visibleOrders.filter(o => o.phase === phase);
    return filtered.sort((a, b) => {
      const av = sortValue(a);
      const bv = sortValue(b);
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      if (sortMode === 'amount') return bv - av;
      return String(av).localeCompare(String(bv), 'de-CH');
    });
  };
  const activeOrder = activeId ? orders.find(o => o.id === activeId) : null;

  const handleDragStart = ({ active }) => setActiveId(active.id);
  const handleDragOver = ({ over }) => setOverId(over?.id || null);

  const handleDragEnd = async ({ active, over }) => {
    setActiveId(null);
    setOverId(null);
    if (!over) return;
    const targetPhaseId = over.id.toString().replace('phase-', '');
    const newPhase = parseInt(targetPhaseId);
    const order = orders.find(o => o.id === active.id);
    if (!order || order.phase === newPhase || isNaN(newPhase)) return;

    // Optimistic update
    setOrders(prev => prev.map(o => o.id === active.id ? { ...o, phase: newPhase } : o));
    try {
      await api.post('/orders/kanban-move', { order_id: active.id, new_phase: newPhase });
      if (newPhase === 5) {
        toast.success('Phase 5: Zahlungseingang – Auftrag archiviert');
        setOrders(prev => prev.filter(o => o.id !== active.id));
      } else {
        toast.success(`Verschoben zu Phase ${newPhase}`);
      }
    } catch {
      toast.error('Verschieben fehlgeschlagen');
      loadOrders();
    }
  };

  const handleExport = () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('yh_token');
    fetch(`${BACKEND_URL}/api/export/orders`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'auftraege.csv'; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => toast.error('Export fehlgeschlagen'));
  };

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="orders-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-black text-3xl text-foreground">Aufträge</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {visibleOrders.length} von {orders.length} aktive Aufträge · Drag & Drop zum Phasenwechsel
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExport} data-testid="export-orders-btn" className="rounded-sm gap-2">
              <Download className="w-4 h-4" />CSV
            </Button>
            {hasPermission('can_create') && (
              <Button onClick={() => navigate('/orders/new')} data-testid="new-order-btn" className="rounded-sm gap-2">
                <Plus className="w-4 h-4" />Neuer Auftrag
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[minmax(260px,1fr)_220px_220px_220px] gap-3 mb-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Aufträge suchen..." className="pl-9 rounded-sm" data-testid="order-search" />
          </div>
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="rounded-sm" data-testid="order-company-filter"><SelectValue placeholder="Firma" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Firmen</SelectItem>
              {monteurCompanies.map(company => <SelectItem key={company} value={company}>{company}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger className="rounded-sm" data-testid="order-customer-filter"><SelectValue placeholder="Verwaltung" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Verwaltungen</SelectItem>
              {customerNames.map(customer => <SelectItem key={customer} value={customer}>{customer}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortMode} onValueChange={setSortMode}>
            <SelectTrigger className="rounded-sm" data-testid="order-sort"><SelectValue placeholder="Sortierung" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="due">Fälligkeit</SelectItem>
              <SelectItem value="company">Firma</SelectItem>
              <SelectItem value="customer">Verwaltung</SelectItem>
              <SelectItem value="amount">Betrag</SelectItem>
              <SelectItem value="created">Erstellt</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="overflow-x-auto -mx-6 px-6">
              <div className="flex gap-4 min-w-max pb-4">
                {PHASES.map(phase => (
                  <PhaseColumn
                    key={phase.id}
                    phase={phase}
                    orders={ordersByPhase(phase.id)}
                    isOver={overId === `phase-${phase.id}`}
                  />
                ))}
              </div>
            </div>
            <DragOverlay>
              {activeOrder ? (
                <div className="bg-card border border-primary rounded-sm p-4 shadow-xl w-72 opacity-95 rotate-1">
                  <span className="text-xs font-mono text-muted-foreground">{activeOrder.order_number}</span>
                  <p className="text-sm font-semibold mt-1">{activeOrder.title}</p>
                  {activeOrder.customer_name && <p className="text-xs text-muted-foreground mt-1">{activeOrder.customer_name}</p>}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
    </Layout>
  );
}
